import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, ShieldCheck, GitBranch, ArrowRight, Terminal } from "lucide-react";

const BASE_HOST = "book.joe-fenix-method.com";

export default function Landing() {
  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2 font-mono-head font-extrabold tracking-tight">
          <span className="grid place-items-center h-8 w-8 rounded bg-primary text-primary-foreground glow-primary">
            <Zap size={18} />
          </span>
          <span className="text-lg">FENIX<span className="text-primary">·</span>ZERO</span>
        </div>
        <Link
          to="/login"
          data-testid="nav-login-link"
          className="font-mono-head text-xs uppercase tracking-widest px-4 py-2 rounded border border-border hover:border-primary hover:text-primary transition-colors"
        >
          Console Login
        </Link>
      </div>

      <div className="max-w-6xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 text-[11px] font-mono-head uppercase tracking-widest text-accent border border-accent/30 rounded-full px-3 py-1 mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 ping-dot" />
            Engine Online · Zero-Click
          </div>
          <h1 className="font-mono-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            High-Speed <span className="text-primary text-glow">Zero-Click</span> URL Routing Engine
          </h1>
          <p className="mt-6 text-muted-foreground text-base sm:text-lg leading-relaxed max-w-xl">
            Clean HTTP 301/307 deep-link redirection that breaks out of in-app
            webviews and forces the client's native browser. No third-party
            tracking — logs stay in-house and route to your inbox.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to="/login"
              data-testid="get-started-btn"
              className="group inline-flex items-center gap-2 bg-primary text-primary-foreground font-mono-head font-semibold px-6 py-3 rounded-md hover:bg-[#E04800] transition-colors glow-primary"
            >
              Open Command Center
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="/go"
              data-testid="test-primary-route-btn"
              className="inline-flex items-center gap-2 border border-border px-6 py-3 rounded-md font-mono-head hover:border-accent hover:text-accent transition-colors"
            >
              <Terminal size={18} /> Test /go Route
            </a>
          </div>

          <div className="mt-10 font-code text-xs sm:text-sm bg-card/70 border border-border rounded-lg p-4 backdrop-blur">
            <div className="text-muted-foreground">$ route --map</div>
            <div className="mt-1">
              <span className="text-accent">{BASE_HOST}</span>
              <span className="text-muted-foreground">/go</span>
              <span className="text-primary"> ⟶ </span>
              <span className="text-accent">{BASE_HOST}</span>
              <span className="text-emerald-400">/zero-stress-method</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="grid gap-4"
        >
          {[
            { icon: Zap, title: "301 / 307 Redirection", desc: "Configurable permanent or temporary status codes per route.", color: "text-primary" },
            { icon: GitBranch, title: "Native Browser Breakout", desc: "Detects Instagram / TikTok / Facebook webviews and forces the OS browser.", color: "text-accent" },
            { icon: ShieldCheck, title: "Zero Third-Party Tracking", desc: "All logs generated internally and delivered by daily email digest.", color: "text-emerald-400" },
          ].map((f, i) => (
            <div
              key={i}
              className="relative rounded-xl border border-border bg-card/80 p-6 backdrop-blur overflow-hidden hover:border-primary/50 transition-colors"
            >
              <div className="absolute top-0 left-0 h-0.5 w-full bg-gradient-to-r from-primary via-accent to-transparent" />
              <f.icon className={`${f.color} mb-3`} size={26} />
              <div className="font-mono-head font-bold">{f.title}</div>
              <div className="text-sm text-muted-foreground mt-1">{f.desc}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}
